import { useHistory } from "react-router";
import { Product, ShippingMethodType } from "@core/types";
import { useIonToast } from "@ionic/react";
import { AddToCartProduct, useCartData } from "../cartDataStore";
import { useAuth } from "../../contexts/auth-context";
import { useConfig } from "../config";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useOrderData } from "../orderDataStore";
import { getProductQuantityStep } from "../../utils/productQuantity";
import { isFastCourierFeatureEnabled } from "../address/taxiMasterShippingUtils";

interface ProductActionsType {
  addToCart: (product: Product) => void;
  showProductDetail: (productId: Product["id"]) => void;
  removeProductFromCart: (productId: Product["id"]) => void;
  setProductQuantity: (productId: Product["id"], quantity: number, product: Product) => void;
}

const useProductCardActions = (): ProductActionsType => {
  const { t } = useTranslation();
  const config = useConfig();
  const history = useHistory();
  const { isUserAuthenticated } = useAuth();
  const [presentToast] = useIonToast();
  const { shippingMethod } = useOrderData();
  const isTaxiMasterTurned = !!(
    isFastCourierFeatureEnabled(config) && shippingMethod?.methodType === ShippingMethodType.taxiMaster
  );

  const {
    addToCart: addToCartStore,
    removeFromCart,
    cartProductsMap,
    getProductsTotalWeight,
    setProductQuantity: setProductQuantityStore,
  } = useCartData();

  const totalWeight = getProductsTotalWeight();

  const showProductDetail: ProductActionsType["showProductDetail"] = (productId) => {
    history.push("/product-detail/" + productId);
  };

  const setProductQuantity: ProductActionsType["setProductQuantity"] = (productId, quantity, product) => {
    if (quantity > product.stockQuantity) {
      presentToast({
        message: t("Product Quantity is Limited!"),
        duration: 600,
      });
      return;
    }

    const currentQuantity = cartProductsMap[product.id]?.quantity ?? 0;
    const diff = quantity - currentQuantity;
    const diffWeight = diff * (Number(product.weight) ?? 0);

    const isWeightExceeded = config.maxWeightForOrderKG && totalWeight + diffWeight > config.maxWeightForOrderKG;

    if (isWeightExceeded && !isTaxiMasterTurned && diff > 0) {
      toast.info(t("Max weight for order is reached kg", { max: config.maxWeightForOrderKG }));
      return;
    }

    setProductQuantityStore(productId, quantity, product as AddToCartProduct, isUserAuthenticated);
  };

  const addToCart: ProductActionsType["addToCart"] = (product) => {
    const cartQuantity = cartProductsMap[product.id]?.quantity ?? 0;
    const step = getProductQuantityStep({
      product,
    });

    const isStockQuantityLimited = product.stockQuantity > cartQuantity && product.stockQuantity - cartQuantity < step;
    const isWeightExceeded =
      config.maxWeightForOrderKG && totalWeight + (Number(product.weight) ?? 0) > config.maxWeightForOrderKG;

    if (isStockQuantityLimited) {
      return;
    }
    // Тост не показываем когда включен такси мастер, т.к. в нем нет ограничения по весу заказа
    else if (isWeightExceeded && !isTaxiMasterTurned) {
      toast.info(t("Max weight for order is reached kg", { max: config.maxWeightForOrderKG }));
      return;
    }

    if (product.stockQuantity === cartQuantity) {
      presentToast({
        message: t("Product Quantity is Limited!"),
        duration: 600,
      });
      return;
    }

    if (product.type === "variable") {
      showProductDetail(product.id);
    } else if (product.stockQuantity > cartQuantity) {
      addToCartStore(product as AddToCartProduct);
    }
  };

  const removeProductFromCart: ProductActionsType["removeProductFromCart"] = (productId) => {
    removeFromCart(productId, isUserAuthenticated);
  };

  return {
    addToCart,
    showProductDetail,
    removeProductFromCart,
    setProductQuantity,
  };
};

export default useProductCardActions;
